import type { fabric } from 'fabric';
import type { FabricEditor } from 'modules/fabric-editor';
import React from 'react';
import ColorField from './color-field';
import InspectorRow from './inspector-row';
import InspectorSection from './inspector-section';
import NumberField from './number-field';
import { useCommonFabricProp } from './use-common-fabric-prop';
import type { FillGradientControl } from './use-fill-gradient';

const MIXED = '(разное)';

interface AppearanceSectionProps {
  objects: fabric.Object[];
  editor: FabricEditor;
  /** Owned by the parent inspector, which also renders the gradient section itself. */
  gradient: FillGradientControl;
}

/**
 * Fill / stroke / thickness / opacity — the appearance controls every fillable
 * shape shares. `rect` and the generic shape inspector render identical rows.
 */
const AppearanceSection: React.FC<AppearanceSectionProps> = ({ objects, editor, gradient }) => {
  const fill = useCommonFabricProp<string>(objects, 'fill', editor);
  const stroke = useCommonFabricProp<string>(objects, 'stroke', editor);
  const strokeWidth = useCommonFabricProp<number>(objects, 'strokeWidth', editor);
  const opacity = useCommonFabricProp<number>(objects, 'opacity', editor);
  // When the gradient is on, `fill` is a gradient (not a hex string), so the plain
  // fill picker is meaningless — the colors are edited in the Gradient section.
  const gradientActive = gradient.enabled.value === true || gradient.enabled.isMixed;

  return (
    <InspectorSection title="Внешний вид">
      {!gradientActive && (
        <InspectorRow label="Заливка">
          <ColorField
            value={fill.isMixed ? undefined : fill.value}
            onChange={fill.setValue}
            placeholder={fill.isMixed ? MIXED : undefined}
          />
        </InspectorRow>
      )}
      <InspectorRow label="Обводка">
        <ColorField
          value={stroke.isMixed ? undefined : stroke.value}
          onChange={stroke.setValue}
          placeholder={stroke.isMixed ? MIXED : undefined}
        />
      </InspectorRow>
      <InspectorRow label="Толщина">
        <NumberField
          value={strokeWidth.isMixed ? undefined : strokeWidth.value}
          onChange={strokeWidth.setValue}
          min={0}
          placeholder={strokeWidth.isMixed ? MIXED : undefined}
        />
      </InspectorRow>
      <InspectorRow label="Прозрачность">
        <NumberField
          value={opacity.isMixed ? undefined : opacity.value}
          onChange={opacity.setValue}
          step={0.05}
          min={0}
          max={1}
          placeholder={opacity.isMixed ? MIXED : undefined}
        />
      </InspectorRow>
    </InspectorSection>
  );
};

export default AppearanceSection;
